import { SentryError } from '@ifixit/sentry';
import type {
   AddCartLinesMutation,
   AddCartLinesMutationVariables,
   GetCartQueryVariables,
   RemoveCartLinesMutationVariables,
   StorefrontClient,
   UpdateCartAttributesMutationVariables,
   UpdateCartLinesMutationVariables,
} from '@ifixit/shopify-storefront-client';

export async function getCart(
   client: StorefrontClient,
   { cartId, country, language }: GetCartQueryVariables
) {
   const { cart } = await client.getCart({ cartId, country, language });
   return cart ?? null;
}

export async function addToCart(
   client: StorefrontClient,
   { cartId, country, lines, language }: AddCartLinesMutationVariables
) {
   const { cartLinesAdd } = await client.addCartLines({ cartId, country, lines, language });
   if (cartLinesAdd && cartLinesAdd.userErrors.length > 0) {
      console.error(cartLinesAdd.userErrors);
   }

   if (cartLinesAdd == null) {
      throw new SentryError('Failed to add to cart', { extra: { cartId, lines } });
   }

   return withSeparatedErrors(cartLinesAdd);
}

export async function updateCartLines(
   client: StorefrontClient,
   { cartId, country, lines, language }: UpdateCartLinesMutationVariables
) {
   const { cartLinesUpdate } = await client.updateCartLines({ cartId, country, lines, language });
   if (cartLinesUpdate && cartLinesUpdate.userErrors.length > 0) {
      console.error(cartLinesUpdate.userErrors);
   }

   if (cartLinesUpdate == null) {
      throw new SentryError('Failed to update cart lines', { extra: { cartId, lines } });
   }

   return withSeparatedErrors(cartLinesUpdate);
}

export async function removeCartLines(
   client: StorefrontClient,
   { cartId, country, lineIds, language }: RemoveCartLinesMutationVariables
) {
   const { cartLinesRemove } = await client.removeCartLines({ cartId, country, lineIds, language });
   if (cartLinesRemove && cartLinesRemove.userErrors.length > 0) {
      console.error(cartLinesRemove.userErrors);
   }

   if (cartLinesRemove == null) {
      throw new SentryError('Failed to remove cart lines', { extra: { cartId, lineIds } });
   }

   return withSeparatedErrors(cartLinesRemove);
}

export async function updateCartAttributes(
   client: StorefrontClient,
   { attributes, cartId, country, language }: UpdateCartAttributesMutationVariables
) {
   const { cartAttributesUpdate } = await client.updateCartAttributes({
      attributes,
      cartId,
      country,
      language,
   });
   if (cartAttributesUpdate && cartAttributesUpdate.userErrors.length > 0) {
      console.error(cartAttributesUpdate.userErrors);
   }

   if (cartAttributesUpdate == null) {
      throw new SentryError('Failed to update cart attributes', { extra: { attributes, cartId } });
   }

   return withSeparatedErrors(cartAttributesUpdate);
}

type CartUserErrors = NonNullable<AddCartLinesMutation['cartLinesAdd']>['userErrors'];

type PartitionedUserErrors = {
   validationErrors: string[];
   userErrors: CartUserErrors;
};

function withSeparatedErrors<T extends { userErrors: CartUserErrors }>(
   result: T
): T & PartitionedUserErrors {
   const { validationErrors, userErrors: separatedUserErrors } = separateValidationErrors(
      result.userErrors
   );
   return {
      ...result,
      validationErrors,
      userErrors: separatedUserErrors,
   };
}

/**
 * Checks if the Shopify mutation errors indicate the cart no longer exists.
 * This happens when a cart expires (10 days of inactivity) or is deleted
 * after checkout completion.
 */
export function isCartNotFoundError(
   userErrors: { code?: string | null; field?: string[] | null }[]
): boolean {
   return userErrors.some(error => error.code === 'INVALID' && error.field?.[0] === 'cartId');
}

function separateValidationErrors(userErrors: CartUserErrors): PartitionedUserErrors {
   return userErrors.reduce(
      (acc: PartitionedUserErrors, error) => {
         if (error.code === 'VALIDATION_CUSTOM') {
            acc.validationErrors.push(error.message);
         } else {
            acc.userErrors.push(error);
         }
         return acc;
      },
      { validationErrors: [], userErrors: [] }
   );
}
